import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient, { setTokens, clearTokens } from '../../utils/apiClient';
import { notifyUserLoggedOut } from '../../utils/socketClient';
import { AuthState, User, LoginRequest, TokenResponse } from '../../types';

// Read tokens defensively — see apiClient for the same dance. localStorage
// can hold the literal string "undefined" from older buggy installs.
const _safeStored = (k: string): string | null => {
  const v = localStorage.getItem(k);
  return v && v !== 'undefined' && v !== 'null' ? v : null;
};
const _at = _safeStored('accessToken');
const _rt = _safeStored('refreshToken');

const initialState: AuthState = {
  user: null,
  accessToken: _at,
  refreshToken: _rt,
  isAuthenticated: !!_at,
  isLoading: false,
  error: null,
  requires2FA: false,
  requiresPasswordChange: false,
  passwordChangeToken: null,
};

type LoginResult =
  | { user: User; tokens: TokenResponse }
  | { requires2FA: true }
  | { requiresPasswordChange: true; passwordChangeToken: string };

export const login = createAsyncThunk<LoginResult, LoginRequest, { rejectValue: string }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const tokenResponse = await apiClient.post<TokenResponse>('/auth/login', credentials);
      // Forced password change takes priority — backend returns no access_token,
      // only a short-lived password_change_token. We must NOT call setTokens here.
      if (tokenResponse.data.requires_password_change && tokenResponse.data.password_change_token) {
        return {
          requiresPasswordChange: true as const,
          passwordChangeToken: tokenResponse.data.password_change_token,
        };
      }
      if (tokenResponse.data.requires_2fa) {
        return { requires2FA: true as const };
      }
      setTokens(tokenResponse.data.access_token, tokenResponse.data.refresh_token);
      const userResponse = await apiClient.get<User>('/auth/me');
      return { user: userResponse.data, tokens: tokenResponse.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Login failed');
    }
  }
);

// Complete the forced password change. Uses the short-lived password_change_token
// (NOT an access token — endpoint accepts it directly in the body).
export const changeForcedPassword = createAsyncThunk<
  { user: User; tokens: TokenResponse },
  { newPassword: string },
  { state: { auth: AuthState }; rejectValue: string }
>(
  'auth/changeForcedPassword',
  async ({ newPassword }, { getState, rejectWithValue }) => {
    try {
      const { passwordChangeToken } = getState().auth;
      if (!passwordChangeToken) {
        return rejectWithValue('Password change token missing. Please log in again.');
      }
      const tokenResponse = await apiClient.post<TokenResponse>('/auth/change-password-forced', {
        password_change_token: passwordChangeToken,
        new_password: newPassword,
      });
      setTokens(tokenResponse.data.access_token, tokenResponse.data.refresh_token);
      const userResponse = await apiClient.get<User>('/auth/me');
      return { user: userResponse.data, tokens: tokenResponse.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Password change failed');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async (_, { getState }) => {
  try {
    const state = getState() as any;
    const userId = state.auth.user?.id;
    if (userId) notifyUserLoggedOut(userId);
  } catch {}
  try { await apiClient.post('/auth/logout'); } catch {} finally { clearTokens(); }
});

export const fetchCurrentUser = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<User>('/auth/me');
      return response.data;
    } catch (error: any) {
      clearTokens();
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch user');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    setUser: (state, action: PayloadAction<User>) => { state.user = action.payload; state.isAuthenticated = true; },
    setCredentials: (state, action: PayloadAction<{ accessToken?: string; refreshToken?: string; access_token?: string; refresh_token?: string; user?: User }>) => {
      const at = action.payload.accessToken || action.payload.access_token;
      const rt = action.payload.refreshToken || action.payload.refresh_token;
      if (at) state.accessToken = at;
      if (rt) state.refreshToken = rt;
      if (action.payload.user) state.user = action.payload.user;
      state.isAuthenticated = true;
    },
    clear2FA: (state) => { state.requires2FA = false; },
    clearPasswordChange: (state) => {
      state.requiresPasswordChange = false;
      state.passwordChangeToken = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        if ('requiresPasswordChange' in action.payload) {
          state.requiresPasswordChange = true;
          state.passwordChangeToken = action.payload.passwordChangeToken;
          state.requires2FA = false;
          state.isAuthenticated = false;
        } else if ('requires2FA' in action.payload) {
          state.requires2FA = true;
          state.requiresPasswordChange = false;
        } else {
          state.requires2FA = false;
          state.requiresPasswordChange = false;
          state.passwordChangeToken = null;
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.accessToken = action.payload.tokens.access_token;
          state.refreshToken = action.payload.tokens.refresh_token;
        }
      })
      .addCase(login.rejected, (state, action) => { state.isLoading = false; state.error = action.payload || 'Login failed'; })
      .addCase(changeForcedPassword.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(changeForcedPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.requiresPasswordChange = false;
        state.passwordChangeToken = null;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.tokens.access_token;
        state.refreshToken = action.payload.tokens.refresh_token;
      })
      .addCase(changeForcedPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Password change failed';
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.requires2FA = false;
        state.requiresPasswordChange = false;
        state.passwordChangeToken = null;
      })
      .addCase(fetchCurrentUser.pending, (state) => { state.isLoading = true; })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => { state.isLoading = false; state.user = action.payload; state.isAuthenticated = true; })
      .addCase(fetchCurrentUser.rejected, (state) => { state.isLoading = false; state.isAuthenticated = false; state.user = null; state.accessToken = null; state.refreshToken = null; });
  },
});

export const { clearError, setUser, setCredentials, clear2FA, clearPasswordChange } = authSlice.actions;
export default authSlice.reducer;
